import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral'
  | 'info'
  | 'primary'
  | 'outline'
  | 'default';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  dot?: boolean;
}

const variantClasses: Record<BadgeVariant, string> = {
  success: 'bg-badge-green-bg text-badge-green-text [--dot-color:var(--color-badge-green-dot)]',
  warning: 'bg-badge-yellow-bg text-warning [--dot-color:var(--color-warning)]',
  danger: 'bg-badge-red-bg text-error [--dot-color:var(--color-error)]',
  neutral: 'bg-outlines text-text-secondary [--dot-color:var(--color-text-secondary)]',
  info: 'bg-info-subtle text-info [--dot-color:var(--color-info)]',
  primary: 'bg-primary-subtle text-primary [--dot-color:var(--color-primary)]',
  // Aliases mapped to productx tokens for backward compatibility with shadcn call sites.
  outline: 'bg-white border border-outlines text-text-secondary [--dot-color:var(--color-text-secondary)]',
  default: 'bg-primary-subtle text-primary [--dot-color:var(--color-primary)]',
};

export function Badge({ variant = 'neutral', dot = true, className, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-badge)] px-2.5 py-1 text-sm font-medium leading-[18px]',
        variantClasses[variant],
        className,
      )}
      {...props}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-[var(--dot-color)]" />}
      {children}
    </span>
  );
}
